import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { TippyTooltipDirective } from '../../directives/tippy-tooltip.directive';

/**
 * Flag toggle for the onboarding review — Figma "Approve/deny onboarding" States (4082:159037,
 * 3 sep export): 32×32 ghost icon button, radius 8, hover fill 8% black, tooltip on hover.
 * Presentational: the host owns the flag list; this only reports the click.
 */
@Component({
  selector: 'dd-review-flag-button',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatIconModule, TippyTooltipDirective],
  template: `
    <button
      type="button"
      class="inline-flex h-8 w-8 flex-none items-center justify-center rounded-lg border-0 bg-transparent
             transition-colors
             hover:bg-black/[0.08] dark:hover:bg-white/[0.08]
             focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2
             disabled:cursor-not-allowed disabled:opacity-40"
      [attr.aria-pressed]="flagged()"
      [attr.aria-label]="ariaLabel()"
      [attr.data-resolved]="resolved() ? 'true' : null"
      [attr.data-cy]="dataCy()"
      [disabled]="disabled()"
      [tippyTooltip]="tooltip()"
      (click)="toggle.emit()">
      <mat-icon class="icon-size-4" [svgIcon]="flagged() ? 'heroicons_solid:flag' : 'heroicons_outline:flag'"></mat-icon>
    </button>
  `,
  styles: `
    /* BOTH colour states live here as plain CSS, not as Tailwind utilities on the button — this
       repo compiles Tailwind with important: true, so an arbitrary-value text colour utility
       would emit !important and permanently beat any other colour rule (measured: the
       un-flagged utility was silently winning over the flagged rule below). aria-pressed
       already carries the flagged state, so key off that attribute instead of a class binding. */
    button {
      color: var(--dd-accent);
    }

    button[aria-pressed='true'] {
      color: var(--dd-badge-warning-fg);
    }

    /* A resolved marking is history until it is reopened, so its button reads muted rather than
       amber. Keyed off the attribute for the same reason as the flagged rule above: Tailwind's
       important utilities would beat a class-bound colour. Both schemes come from the token. */
    button[data-resolved='true'] {
      color: var(--dd-card-muted-fg);
    }
  `,
})
export class ReviewFlagButtonComponent {
  /** Name of the part this flag belongs to (document title, section title) — read by the tooltip and screen readers. */
  readonly label = input.required<string>();
  readonly flagged = input(false);
  /** The carrier resubmitted on this marking: the click reopens it instead of removing it. */
  readonly resolved = input(false);
  readonly disabled = input(false);
  readonly dataCy = input<string | null>(null);

  /**
   * Flag toggle requested. Named `toggle` per the review-flag task-2 contract that the mounting
   * review component codes against. It shadows the native `toggle` DOM event (`<details>`/popover),
   * which this component never fires — Angular resolves `(toggle)` to this output on the element,
   * same precedent as `getting-started-panel.component.ts`.
   */
  // eslint-disable-next-line @angular-eslint/no-output-native
  readonly toggle = output<void>();

  readonly tooltip = computed(() => {
    if (this.resolved()) return 'Opnieuw markeren';
    return this.flagged() ? 'Markering verwijderen' : 'Markeren voor afwijzing';
  });
  readonly ariaLabel = computed(() => {
    if (this.resolved()) return `${this.label()} opnieuw markeren`;
    return this.flagged() ? `Markering van ${this.label()} verwijderen` : `${this.label()} markeren voor afwijzing`;
  });
}
